import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/products";
import { useStock } from "@/context/StockContext";
import { NotifyMe } from "./NotifyMe";

export const ShadePicker = ({ product, value, onChange, size = "sm" }) => {
  const { isSoldOut } = useStock();
  return (
  <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Shade">
    {product.shades.map((s) => {
      const active = s.name === value;
      const out = isSoldOut(product.id, s.name);
      const slug = s.name.toLowerCase().replace(/\s+/g, "-");
      return (
        <button key={s.name} onClick={() => onChange(s.name)} title={out ? `${s.name} — sold out` : s.name} role="radio" aria-checked={active}
          data-testid={`shade-${product.id}-${slug}`}
          className={`relative overflow-hidden rounded-full transition-transform duration-200 hover:scale-110 ${size === "lg" ? "w-10 h-10" : "w-7 h-7"} ${active ? "ring-2 ring-offset-2 ring-offset-transparent ring-current" : ""} ${out ? "opacity-60" : ""}`}
          style={{ background: s.hex }}>
          {out && <span className="absolute left-1/2 top-[-20%] h-[140%] w-[2px] -translate-x-1/2 rotate-45 bg-[#FAF7F2]" />}
        </button>
      );
    })}
  </div>
  );
};

export function ProductCard({ product, index }) {
  const [shade, setShade] = useState(product.shades[0].name);
  const { add } = useCart();
  const { isSoldOut } = useStock();
  const out = isSoldOut(product.id, shade);
  const addToBag = () => {
    add(product.id, shade);
    toast.success(`${product.name} · ${shade} added to your bag`);
  };

  return (
    <motion.article initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className={`group text-[#FAF7F2] ${index === 1 ? "md:mt-24" : ""}`} data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} className="block relative" data-testid={`product-link-${product.id}`}>
        <div className="absolute -inset-6 rounded-full bg-[radial-gradient(circle,rgba(235,177,182,0.35),transparent_65%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative aspect-[3/4] arch overflow-hidden bg-[#4A2E2B]">
          <img src={product.image} alt={product.name} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.07]" />
          {product.compareAt && (
            <span className="absolute top-6 left-1/2 -translate-x-1/2 overline text-[#281715] bg-[#EBB1B6] rounded-full px-3 py-1.5">Gift set</span>
          )}
        </div>
        <span className="absolute -bottom-4 right-6 font-display italic text-7xl text-[#EBB1B6]/90 leading-none">0{index + 1}</span>
      </Link>
      <div className="mt-10 flex items-start justify-between gap-4">
        <div>
          <p className="overline text-[#EBB1B6]">{product.kind}</p>
          <h3 className="font-display text-3xl mt-2" data-testid={`product-name-${product.id}`}>{product.name}</h3>
        </div>
        <div className="text-right">
          <p className="font-display text-2xl" data-testid={`product-price-${product.id}`}>{money(product.price)}</p>
          {product.compareAt && <p className="text-sm text-[#D9C4BC] line-through">{money(product.compareAt)}</p>}
        </div>
      </div>
      <div className="mt-5 flex items-center justify-between gap-4 text-[#EBB1B6]">
        <ShadePicker product={product} value={shade} onChange={setShade} />
        {!out && (
        <motion.button whileTap={{ scale: 0.92 }} onClick={addToBag} data-testid={`add-to-bag-${product.id}`}
          className="flex items-center gap-2 rounded-full border border-[#EBB1B6]/60 text-[#FAF7F2] px-4 h-10 text-sm hover:bg-[#EBB1B6] hover:text-[#281715] transition-colors duration-200">
          <Plus className="w-4 h-4" /> Add
        </motion.button>
        )}
      </div>
      {out ? <div className="mt-4"><NotifyMe key={shade} productId={product.id} shade={shade} dark /></div> : <p className="mt-3 text-sm text-[#D9C4BC]">{shade}</p>}
    </motion.article>
  );
}
